"use client";
import { Badge } from "@cmp-src/data-display/Badge/Badge";
import type { IResource, Space } from "@gettactic/api";
import { logError } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import {
  useFloorSpacesQuery,
  useInvalidateZones,
  useZonesQuery
} from "@gettactic/hooks";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { RegionPreview } from "@/features/settings/components/ZonesPage/RegionPreview";
import { UtilizationCell } from "@/features/settings/components/ZonesPage/UtilizationCell";
import { useUtilizationBy } from "@/features/settings/components/ZonesPage/useZoneUsage";
import { ZoneSwatch } from "@/features/settings/components/ZonesPage/ZoneSwatch";
import { formatSqm } from "@/features/settings/components/ZonesPage/zoneTree";
import { api } from "@/lib/api/api";

const KIND_LABELS: Record<Space["kind"], string> = {
  room: "Room",
  open_area: "Open area",
  corridor: "Corridor"
};

export interface FloorSpacesPanelProps {
  floor: IResource;
}

/**
 * The spaces (rooms, open areas, corridors) drawn on a floor's active map,
 * with their desks, area and the zone each resolves to. Admins can type in a
 * real-world area when the map is not calibrated or the polygon is rough.
 */
export const FloorSpacesPanel: React.FC<FloorSpacesPanelProps> = ({
  floor
}) => {
  const { data: spaces, isLoading } = useFloorSpacesQuery(floor.id);
  const { data: zones } = useZonesQuery();
  const floorFilter = React.useMemo(
    () => [{ dimension: "floor", in: [floor.id] }],
    [floor.id]
  );
  const utilization = useUtilizationBy("space", floorFilter);
  const zoneById = React.useMemo(
    () => new Map((zones ?? []).map((zone) => [zone.id, zone])),
    [zones]
  );

  if (isLoading) {
    return <p className="py-6 text-neutral-500 text-sm">Loading spaces…</p>;
  }

  if (!spaces || spaces.length === 0) {
    return (
      <div className="py-6 text-neutral-500 text-sm">
        <p className="font-medium text-neutral-700">
          No spaces on this floor yet.
        </p>
        <p className="mt-1">
          Draw zones (rooms, open areas, corridors) on the floor map in the map
          builder and save: each becomes a space here, with its desks and area.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-neutral-100 py-2">
      {spaces.map((space) => (
        <SpaceRow
          key={space.id}
          floor={floor}
          space={space}
          zone={
            space.effective_zone_id
              ? zoneById.get(space.effective_zone_id)
              : undefined
          }
          isInherited={!space.zone_id && !!space.effective_zone_id}
          utilization={
            utilization.canSee ? (
              <UtilizationCell
                usage={utilization.byMember.get(space.id)}
                measure={utilization.measure}
                isLoading={utilization.isLoading}
              />
            ) : null
          }
        />
      ))}
    </div>
  );
};

interface SpaceRowProps {
  floor: IResource;
  space: Space;
  zone?: { name: string; color: string; icon: string | null };
  isInherited: boolean;
  /** Desk utilization over the last 30 days, when the user can see analytics. */
  utilization?: React.ReactNode;
}

const SpaceRow: React.FC<SpaceRowProps> = ({
  floor,
  space,
  zone,
  isInherited,
  utilization
}) => {
  const invalidateZones = useInvalidateZones();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [override, setOverride] = React.useState(
    space.area_sqm_override === null ? "" : String(space.area_sqm_override)
  );
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setOverride(
      space.area_sqm_override === null ? "" : String(space.area_sqm_override)
    );
  }, [space.area_sqm_override]);

  const saveOverride = async () => {
    const trimmed = override.trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (Number.isNaN(value) || value < 0)) {
      toast.error("Area must be a positive number of square metres");
      return;
    }
    if (value === space.area_sqm_override) return;
    setIsSaving(true);
    try {
      await api.client.zones.updateSpace(floor.id, space.id, {
        area_sqm_override: value
      });
      await invalidateZones();
      toast.success(value === null ? "Area override cleared" : "Area saved");
    } catch (error) {
      logError("Error saving space area:", error);
      toast.error("The area could not be saved");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="py-2">
      <div
        className="flex items-center gap-3 text-sm"
        style={{ paddingLeft: `${space.depth * 16}px` }}
      >
        <button
          type="button"
          className="text-neutral-400 hover:text-neutral-700"
          onClick={() => setIsExpanded((value) => !value)}
          aria-label={isExpanded ? "Hide preview" : "Show preview"}
        >
          {isExpanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-neutral-800">
              {space.name}
            </span>
            <Badge variant="outline">{KIND_LABELS[space.kind]}</Badge>
            {space.has_conflict ? (
              <Badge variant="destructive">Overlaps another space</Badge>
            ) : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-neutral-500 text-xs">
            <span>
              {space.counts.desks} desk{space.counts.desks === 1 ? "" : "s"}
            </span>
            {space.counts.meeting_rooms > 0 ? (
              <span>
                {space.counts.meeting_rooms} room
                {space.counts.meeting_rooms === 1 ? "" : "s"}
              </span>
            ) : null}
            <span>
              {formatSqm(space.effective_area_sqm)}
              {space.area_sqm === null && space.area_sqm_override === null
                ? " · save the map in the builder to compute the area"
                : ""}
            </span>
            {zone ? (
              <span className="inline-flex items-center gap-1">
                <ZoneSwatch
                  color={zone.color}
                  icon={zone.icon}
                  className="h-3.5 w-3.5"
                />
                {zone.name}
                {isInherited ? " (inherited)" : ""}
              </span>
            ) : null}
            {utilization ? (
              <span className="inline-flex items-center gap-1">
                Utilization {utilization}
              </span>
            ) : null}
          </div>
        </div>
        <label className="flex items-center gap-1 text-neutral-500 text-xs">
          <span>Area m²</span>
          <input
            type="number"
            min={0}
            step="0.1"
            value={override}
            placeholder={
              space.area_sqm === null ? "—" : space.area_sqm.toFixed(1)
            }
            disabled={isSaving}
            onChange={(event) => setOverride(event.target.value)}
            onBlur={saveOverride}
            onKeyDown={(event) => {
              if (event.key === "Enter")
                (event.target as HTMLInputElement).blur();
            }}
            className={cn(
              "h-7 w-20 rounded-md border border-neutral-200 px-2 text-right text-neutral-800 text-xs",
              isSaving && "opacity-60"
            )}
          />
        </label>
      </div>
      {isExpanded ? (
        <RegionPreview
          floor={floor}
          space={space}
          color={zone?.color}
          className="mt-2 h-48 w-full"
        />
      ) : null}
    </div>
  );
};
